/**
 * The instruction line above a problem ("Solve for x", "Find the value if
 * x = 3") and what a screen reader says for a whole problem.
 *
 * Text comes from core instructionText(). Params whose name ends in "Math"
 * are MathMarkup and render as math, so they are swapped for sentinel tokens
 * before substitution and the result is split back into text and math parts.
 */
import { instructionText, type Locale, type MathMarkup, type Problem } from '@dropstack/core';
import { speechFor } from './mathView.ts';

export type InstructionPart = { kind: 'text'; text: string } | { kind: 'math'; markup: MathMarkup };

/** Sentinel wrapped around a Math param's name; control characters never occur in instruction text. */
const MARK = '\u0001';
const SENTINEL = new RegExp(`${MARK}(\\w+)${MARK}`);

const isMathParam = (name: string) => name.endsWith('Math');

/** Split a problem's instruction into text and math parts (empty when the problem has none). */
export function instructionParts(instruction: Problem['instruction'], locale: Locale): InstructionPart[] {
  if (!instruction) return [];
  const params = instruction.params ?? {};
  const tokenized: Record<string, string | number> = {};
  for (const [name, value] of Object.entries(params)) tokenized[name] = isMathParam(name) ? `${MARK}${name}${MARK}` : value;

  // split() with a capturing group alternates: text, param name, text, param name, …
  const pieces = instructionText(instruction.key, tokenized, locale).split(SENTINEL);
  const parts: InstructionPart[] = [];
  pieces.forEach((piece, i) => {
    if (i % 2 === 1) parts.push({ kind: 'math', markup: String(params[piece] ?? '') });
    else if (piece) parts.push({ kind: 'text', text: piece });
  });
  return parts;
}

/** The instruction as plain speech ("Find the value if n equals 1"). */
export function instructionSpeech(parts: readonly InstructionPart[], locale: Locale): string {
  return parts
    .map((p) => (p.kind === 'text' ? p.text : speechFor(p.markup, locale)))
    .join('')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Everything a screen reader needs for a problem: instruction, then the expression. */
export function problemSpeech(problem: Pick<Problem, 'instruction' | 'prompt'>, locale: Locale): string {
  const instruction = instructionSpeech(instructionParts(problem.instruction, locale), locale);
  const expression = speechFor(problem.prompt, locale);
  return instruction ? `${instruction}: ${expression}` : expression;
}
